import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { BiRefresh } from 'react-icons/bi';
import { FaCheck, FaExclamationTriangle, FaInfoCircle, FaEnvelope, FaFileAlt } from 'react-icons/fa';
import { IoIosRepeat } from 'react-icons/io';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import Select from '../../../components/textFields/AppSelect';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { KTSVG } from '../../..//_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import { constraintConfig } from '../../../constraintConfig';
import { useLazyGetExpenseForReportQuery, useLazyGetExpenseItemByExpenseIdQuery, useLazyGetExpenseItemsByExpenseItemIdQuery } from '../../../services/ExpenseApi';
import { useLazyGetReceiptVaultUploadRulesQuery } from '../../../services/authApi';
import { useGetAllDraftInvoicesQuery } from '../../../services/InvoiceDataDraftApi';
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent';
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent';
import labelKey from '../../localization/label.json';
import ExpenseDetailModal from '../expenseItems/ExpenseDetailModal';
import { UpdateExpenseItemModal } from '../expenseItems/update-single-Item/UpdateExpenseItemModal';
import { timePeriodOptions } from '../reportAnalytics/ReportFilterOptions';
import AddInvoiceModal from './AddInvoiceModal';
import { getEffectiveLineItemId } from './allReceiptsActionHelpers';
import DeleteInvoiceModal from './DeleteInvoiceModal';
import RetryInvoiceModal from './RetryInvoiceModal';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import SsHorizontalScroll from '../../../components/scroll/SsHorizontalScroll';
import SingleItemDetailModal from '../expenseItems/update-single-Item/SingleItemDetailModal';
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp';
import heic2any from 'heic2any';

interface LightboxData {
    imageURL: string;
}

// Helper function to check if file is MSG or EML
const isMsgOrEml = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lowerURL = fileURL.toLowerCase();
    return lowerURL.endsWith('.msg') || lowerURL.endsWith('.eml');
};

// Helper function to check if file is HEIC
const isHeicFile = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lowerURL = fileURL.toLowerCase();
    return lowerURL.endsWith('.heic') || lowerURL.endsWith('.heif');
};

// Helper function to get file extension
const getFileExtension = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const ext = fileURL.toLowerCase().split('.').pop();
    return ext?.toUpperCase() || 'File';
};

// Helper function to get filename from URL
const getFileNameFromURL = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const parts = fileURL.split('/');
    return parts[parts.length - 1] || 'File';
};

const Invoices = () => {
    const currentDate = moment().format('MM/DD/YYYY');
    const threeDaysAgo = moment().subtract(3, 'days').format('MM/DD/YYYY');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxData, setLightboxData] = useState<LightboxData>({ imageURL: '' });
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [heicPreviewUrls, setHeicPreviewUrls] = useState<{ [key: string]: string }>({});

    const [filterByStatus, setFilterByStatus] = useState(0);
    const [filterByStartDate, setFilterByStartDate] = useState(threeDaysAgo);
    const [filterByEndDate, setFilterByEndDate] = useState(currentDate);
    const [filterByTimePeriod, setFilterByTimePeriod] = useState('');
    const [searchStr, setSearchStr] = useState('');

    const handleSearchChange = (e: any) => {
        setSearchStr(e.target.value);
    };

    const [triggerItemsById, { data: itemsByIdData }] = useLazyGetExpenseItemByExpenseIdQuery()
    const [triggerexpenseReportData, { data: expenseReportData }] = useLazyGetExpenseForReportQuery()
    const [triggerItemById, { data: itemByIdData, isFetching: itemByIdDataFetching }] = useLazyGetExpenseItemsByExpenseItemIdQuery()
    const [showUpdateExpenseItemModal, setShowUpdateExpenseItemModal] = useState(false)

    const { data, isLoading, refetch, isSuccess: draftsLoaded } = useGetAllDraftInvoicesQuery({
        filterByStatus: filterByStatus || 0,
        startDate: filterByStartDate,
        endDate: filterByEndDate,
        timePeriod: filterByTimePeriod,
        searchStr: searchStr || undefined,
    });

    const [prefetchReceiptVaultRules] = useLazyGetReceiptVaultUploadRulesQuery()
    useEffect(() => {
        if (!draftsLoaded) return
        prefetchReceiptVaultRules(undefined, true)
    }, [draftsLoaded, prefetchReceiptVaultRules])

    // Convert HEIC files to preview format
    useEffect(() => {
        if (data?.result) {
            const heicFiles = data.result.filter((item: any) => isHeicFile(item.fileURL));

            heicFiles.forEach(async (item: any) => {
                if (!heicPreviewUrls[item.invoiceDataDraftId]) {
                    try {
                        const response = await fetch(item.fileURL);
                        const blob = await response.blob();
                        const convertedBlob = await heic2any({
                            blob: blob,
                            toType: 'image/jpeg',
                            quality: 0.8
                        });
                        const reader = new FileReader();
                        reader.onload = () => {
                            setHeicPreviewUrls(prev => ({
                                ...prev,
                                [item.invoiceDataDraftId]: reader.result as string
                            }));
                        };
                        reader.readAsDataURL(convertedBlob as Blob);
                    } catch (error) {
                        console.error('Error converting HEIC:', error);
                    }
                }
            });
        }
    }, [data]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (data?.result?.some((item: any) => item.ocrReadStatus === 0)) {
                refetch();
            }
        }, 15000);

        return () => clearInterval(interval);
    }, [data, refetch]);

    const [showAddUpdateDepartmentModal, setShowAddUpdateDepartmentModal] = useState(false)
    const [showDepartmentEdit, setShowDepartmentEdit] = useState({})
    const departmentModalHandler = () => {
        setShowAddUpdateDepartmentModal((prev) => !prev);
    };
    const [showAttachmentDeleteModal, setShowAttachmentDeleteModal] = useState(false)
    const [attachmentDeleteData, setAttachmentDeleteData] = useState<number | null>(null);

    const deleteHandler = () => {
        setShowAttachmentDeleteModal((prev) => !prev);
    };
    const [showRetryInvoiceAttachmentModal, setShowRetryInvoiceAttachmentModal] = useState(false)
    const retryInvoiceHandler = () => {
        setShowRetryInvoiceAttachmentModal((prev) => !prev);
    };

    const [showExpenseDetailModal, setShowExpenseDetailModal] = useState(false)

    const expenseDetailHandler = () => {
        setShowExpenseDetailModal((prev) => !prev);
    };
    const [showExpenseItemDetailModal, setShowExpenseItemDetailModal] = useState(false)
    const [selectedExpenseItemId, setSelectedExpenseItemId] = useState<number | null>(null)

    const expenseItemModalHandler = () => {
        setShowUpdateExpenseItemModal((prev) => !prev);
    };
    const [viewOnlyData, setViewOnlyData] = useState(true)

    const handleViewLineItem = useCallback(
        async (expenseItemId?: number | null, opts?: { includeDeleted?: boolean }) => {
            const id = Number(expenseItemId || 0);
            if (!id) {
                toast.warning('No line item is linked to this receipt.');
                return;
            }
            setSelectedExpenseItemId(id);
            setShowExpenseItemDetailModal(true);
            try {
                await triggerItemById({
                    expenseItemId: id,
                    ...(opts?.includeDeleted ? { includeDeleted: true } : {}),
                }).unwrap();
            } catch (error) {
                console.error('Failed to fetch line item:', error);
                toast.error('Unable to load line item details');
            }
        },
        [triggerItemById]
    );

    const [prevModalState, setPrevModalState] = useState({
        addUpdateDepartmentModal: false,
        showAttachmentDeleteModal: false,
        showRetryInvoiceAttachmentModal: false,
        showExpenseDetailModal: false,
        showUpdateExpenseItemModal: false
    });

    useEffect(() => {
        setPrevModalState({
            addUpdateDepartmentModal: showAddUpdateDepartmentModal,
            showAttachmentDeleteModal: showAttachmentDeleteModal,
            showRetryInvoiceAttachmentModal: showRetryInvoiceAttachmentModal,
            showExpenseDetailModal: showExpenseDetailModal,
            showUpdateExpenseItemModal: showUpdateExpenseItemModal
        });
    }, [showAddUpdateDepartmentModal, showAttachmentDeleteModal, showRetryInvoiceAttachmentModal, showExpenseDetailModal, showUpdateExpenseItemModal]);

    useEffect(() => {
        if (
            (prevModalState.addUpdateDepartmentModal && !showAddUpdateDepartmentModal) ||
            (prevModalState.showAttachmentDeleteModal && !showAttachmentDeleteModal) ||
            (prevModalState.showRetryInvoiceAttachmentModal && !showRetryInvoiceAttachmentModal) ||
            (prevModalState.showExpenseDetailModal && !showExpenseDetailModal) ||
            (prevModalState.showUpdateExpenseItemModal && !showUpdateExpenseItemModal)
        ) {
            refetch();
        }
    }, [
        showAddUpdateDepartmentModal,
        showAttachmentDeleteModal,
        showRetryInvoiceAttachmentModal,
        showExpenseDetailModal,
        showUpdateExpenseItemModal,
        prevModalState.addUpdateDepartmentModal,
        prevModalState.showAttachmentDeleteModal,
        prevModalState.showRetryInvoiceAttachmentModal,
        prevModalState.showExpenseDetailModal,
        prevModalState.showUpdateExpenseItemModal,
        refetch,
    ]);
    const handleRefresh = () => {
        refetch()
    };

    const isPdfFile = (filePath: string | undefined) => {
        if (!filePath) return false;
        const fileExtension = filePath.toLowerCase().split('.').pop();
        return fileExtension === 'pdf';
    };

    const openPdfInBrowserOrLightbox = (attachmentFile: string | undefined) => {
        if (attachmentFile) {
            if (isPdfFile(attachmentFile)) {
                window.open(attachmentFile, '_blank');
            } else if (isMsgOrEml(attachmentFile)) {
                window.open(attachmentFile, '_blank');
            } else if (isHeicFile(attachmentFile)) {
                // For HEIC files, use the converted preview URL or open in new tab
                const draftId = data?.result?.find((item: any) => item.fileURL === attachmentFile)?.invoiceDataDraftId;
                if (draftId && heicPreviewUrls[draftId]) {
                    setLightboxData({ imageURL: heicPreviewUrls[draftId] });
                    setLightboxOpen(true);
                } else {
                    window.open(attachmentFile, '_blank');
                }
            } else {
                setLightboxData({ imageURL: attachmentFile });
                setLightboxOpen(true);
            }
        }
    };

    const handleDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');

        setFilterByStartDate(startDateFormatted);
        setFilterByEndDate(endDateFormatted);
        if (typeof window !== 'undefined' && (window.innerWidth < 992 || window.matchMedia('(max-width: 991.98px)').matches)) {
            setShowFilters(false);
        }
    };
    const handleCancelDateSelection = (event: any, picker: any) => {
        setFilterByStartDate("");
        setFilterByEndDate("");
        if (typeof window !== 'undefined' && (window.innerWidth < 992 || window.matchMedia('(max-width: 991.98px)').matches)) {
            setShowFilters(false);
        }
    };
    const handleTimePeriodChange = (selectedOption: any) => {
        setFilterByTimePeriod(selectedOption.value);
        setFilterByStartDate('')
        setFilterByEndDate('')
    };

    const [activeTab, setActiveTab] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const handleTabChange = (status: number) => {
        setActiveTab(status);
        setFilterByStatus(status);
    };
    useEffect(() => {
        if (activeTab) {
            refetch();
        }
    }, [activeTab]);

    return (
        <>
            <ExpenseDetailModal
                show={showExpenseDetailModal}
                handleClose={() => setShowExpenseDetailModal(false)}
                data={expenseReportData?.result}
                expenseItems={itemsByIdData?.result}
                refetch={refetch} />
            <UpdateExpenseItemModal
                show={showUpdateExpenseItemModal}
                handleClose={() => setShowUpdateExpenseItemModal(false)}
                data={itemByIdData?.result}
                viewOnlyData={viewOnlyData} />
            <SingleItemDetailModal
                key={selectedExpenseItemId || 'line-item-detail'}
                show={showExpenseItemDetailModal}
                handleClose={() => {
                    setShowExpenseItemDetailModal(false);
                    setSelectedExpenseItemId(null);
                }}
                data={
                    selectedExpenseItemId &&
                    itemByIdData?.result &&
                    Number(itemByIdData.result.expenseItemId) === Number(selectedExpenseItemId)
                        ? itemByIdData.result
                        : null
                }
                viewOnlyData={true}
                isFetching={
                    itemByIdDataFetching ||
                    (!!selectedExpenseItemId &&
                        !(
                            itemByIdData?.result &&
                            Number(itemByIdData.result.expenseItemId) === Number(selectedExpenseItemId)
                        ))
                } />
            <DeleteInvoiceModal
                show={showAttachmentDeleteModal}
                handleClose={() => setShowAttachmentDeleteModal(false)}
                data={attachmentDeleteData}
            />
            <RetryInvoiceModal
                show={showRetryInvoiceAttachmentModal}
                handleClose={() => setShowRetryInvoiceAttachmentModal(false)}
                data={attachmentDeleteData}
            />
            <AddInvoiceModal
                show={showAddUpdateDepartmentModal}
                handleClose={() => setShowAddUpdateDepartmentModal(false)}
                data={showDepartmentEdit} />
            <Tooltip id="refreshData" place="top" />
            <Tooltip id="retryInvoice" place="top" />
            <Tooltip id="reason" place="top" />
            <Tooltip id="reportId" place="top" />
            <Tooltip id="lineItem" place="top" />
            <Tooltip id="clickReceipt" place="top" />

            {lightboxOpen && (
                <Lightbox
                    mainSrc={lightboxData.imageURL}
                    onCloseRequest={() => setLightboxOpen(false)}
                    imageCaption="Attachment"
                    enableZoom={true}
                    imagePadding={50}
                />
            )}

            <div className='card mb-5'>
                <div className='card-header border-0 pt-6 align-items-center'>
                    <div className='d-flex flex-column flex-lg-row flex-wrap align-items-stretch align-items-lg-center justify-content-between gap-2 w-100 ss-onboarding-toolbar ss-receipt-vault-toolbar'>
                        <div className='d-flex gap-2 align-items-center min-w-0 w-100 w-lg-auto ss-receipt-vault-toolbar__tabs'>
                            <SsHorizontalScroll
                                className='flex-grow-1 min-w-0 w-100 w-lg-auto'
                                ariaLabel='Receipt status tabs'
                            >
                            <div className='ss-user-tab-group btn-group' role='group' aria-label='Receipt status tabs' style={{ minWidth: 'max-content' }}>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.all ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.all)}
                                >
                                    {labelKey.all}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === 0 ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(0)}
                                >
                                    {labelKey.all} (Except Attached & Submitted)
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.readyToUse ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.readyToUse)}
                                >
                                    {labelKey.readyToUse}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.attached ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.attached)}
                                >
                                    {labelKey.attached}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.submitted ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.submitted)}
                                >
                                    {labelKey.submitted}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.failed ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.failed)}
                                >
                                    {labelKey.failed}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.alreadyUsed ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.alreadyUsed)}
                                >
                                    {labelKey.alreadyUsed}
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm text-nowrap ${activeTab === constraintConfig.invoiceStatus.futureReceipt ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleTabChange(constraintConfig.invoiceStatus.futureReceipt)}
                                >
                                    {labelKey.futureReceipt}
                                </button>
                            </div>
                            </SsHorizontalScroll>
                        </div>

                        <div className='ss-mobile-scroll-tabs w-100 w-lg-auto ss-receipt-vault-toolbar__actions ms-lg-auto'>
                        <div className='d-flex justify-content-start align-items-center flex-nowrap gap-2 ms-lg-auto ss-user-list-toolbar__actions' style={{ minWidth: 'max-content' }}>
                            <SearchBarComponent
                                placeholder='Receipt'
                                value={searchStr}
                                onChange={handleSearchChange}
                                className='ss-user-toolbar-search-input'
                                mainClassName='ss-table-search--sm my-0'
                            />
                            <RefreshComponent onClick={handleRefresh} />
                            {showFilters && (
                                <DateRangeComp
                                    startDate={filterByStartDate || moment()}
                                    endDate={filterByEndDate || moment()}
                                    onApply={handleDateRangeChange}
                                    onCancel={handleCancelDateSelection}
                                    value={filterByStartDate && filterByEndDate ? `${filterByStartDate} - ${filterByEndDate}` : 'MM/DD/YYYY'}
                                />
                            )}
                            {!showFilters ? (
                                <button
                                    type="button"
                                    className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap flex-shrink-0"
                                    onClick={() => setShowFilters(true)}
                                >
                                    <i className="fa fa-filter me-1 text-white" />
                                    Show Filters
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap flex-shrink-0"
                                    onClick={() => setShowFilters(false)}
                                >
                                    <i className="fa fa-eye-slash me-1 text-white" />
                                    Hide Filters
                                </button>
                            )}
                            <button
                                type='button'
                                className='btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap flex-shrink-0'
                                onClick={() => {
                                    setShowDepartmentEdit(true);
                                    departmentModalHandler();
                                }}
                            >
                                <i className='fa fa-plus me-1 text-white' />
                                Upload Receipt
                            </button>
                        </div>
                        </div>
                    </div>
                </div>

                <div className='card-body py-8'>
                    <div className="row" style={{ minHeight: "80vh" }}>
                        {!isLoading && (
                            <>
                                {data?.result?.length > 0 ? (
                                    <>
                                        {data?.result?.map((item: any, index: number) => {
                                            const isMsgOrEmlFile = isMsgOrEml(item.fileURL);
                                            const isHeicFileCheck = isHeicFile(item.fileURL);
                                            const fileName = getFileNameFromURL(item.fileURL);
                                            const heicPreviewUrl = heicPreviewUrls[item.invoiceDataDraftId];

                                            return (
                                                <div className="col-md-6 col-lg-4 col-xl-4 mt-4" key={index}>
                                                    <div className="card card-xl-stretch mb-xl-8" style={{ border: "1px solid #cccccc" }}>
                                                        <div className='position-relative' style={{ height: 450 }}>
                                                            <div className='position-absolute my-3 mx-3 z-index-1' style={{ left: 0, bottom: 0 }}>
                                                                {item.ocrReadStatus === 0 ?
                                                                    <span className="btn btn-sm btn-primary fs-7 fw-semibold w-max-content">{labelKey.processing}... <Spinner animation="border" variant="white" size="sm" /> </span>
                                                                    : item.ocrReadStatus === 1 ?
                                                                        <span className="btn btn-sm btn-success fs-7 fw-semibold w-max-content">{labelKey.readyToUse} <FaCheck className='ms-2' /> </span>
                                                                        : item.ocrReadStatus === 2 ?
                                                                            <span className="btn btn-sm btn-danger fs-7 fw-semibold w-max-content">{labelKey.failed} <FaExclamationTriangle className='ms-2' /> </span>
                                                                            : item.ocrReadStatus === 3 ?
                                                                                <span className="btn btn-sm btn-secondary fs-7 fw-bold w-max-content">{labelKey.attached}</span>
                                                                                : item.ocrReadStatus === 4 ?
                                                                                    <span className="btn btn-sm btn-success fs-7 fw-bold w-max-content">{labelKey.submitted}</span>
                                                                                    : item.ocrReadStatus === 5 ?
                                                                                        <span className="btn btn-sm btn-warning fs-7 fw-bold w-max-content">{labelKey.alreadyUsed} </span>
                                                                                        : item.ocrReadStatus === 6 ?
                                                                                            <span className="btn btn-sm btn-info fs-7 fw-bold w-max-content">{labelKey.futureReceipt} </span>
                                                                                            : item.ocrReadStatus === 7 ?
                                                                                                <span className="btn btn-sm btn-info fs-7 fw-bold w-max-content">{labelKey.cCReceipt} </span>
                                                                                                : null
                                                                }
                                                            </div>
                                                            {item?.expenseItemId === null || (item.ocrReadStatus !== 3 && item.ocrReadStatus !== 4) ?
                                                                <div
                                                                    className='btn btn-icon btn-bg-light hover-danger btn-sm position-absolute m-4'
                                                                    style={{ left: 0, zIndex: 12, top: 6 }}
                                                                    onClick={() => {
                                                                        setAttachmentDeleteData(item?.invoiceDataDraftId)
                                                                        deleteHandler();
                                                                    }}
                                                                >
                                                                    <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-hover-danger' svgClassName='svg-danger' />
                                                                </div>
                                                                : null}
                                                            {item?.noOfRetry <= 2 ?
                                                                <>
                                                                    {item?.ocrReadStatus === 2 &&
                                                                        <div
                                                                            data-tooltip-id="retryInvoice" data-tooltip-content='Retry Invoice'
                                                                            className='btn btn-icon btn-bg-warning btn-sm position-absolute m-4'
                                                                            style={{ right: 0, zIndex: 12, top: 6 }}
                                                                            onClick={() => {
                                                                                setAttachmentDeleteData(item?.invoiceDataDraftId)
                                                                                retryInvoiceHandler();
                                                                            }}
                                                                        >
                                                                            <BiRefresh className='h-15px w-15px' />
                                                                        </div>
                                                                    }
                                                                </>
                                                                : null}

                                                            {/* PDF logic */}
                                                            {item.fileURL.endsWith('.pdf') ? (
                                                                <div
                                                                    onClick={() => openPdfInBrowserOrLightbox(item?.fileURL)}
                                                                    style={{ cursor: "pointer", position: "relative", width: "100%", height: "100%" }}
                                                                    data-tooltip-content='Click to view the receipt.' data-tooltip-id="clickReceipt">
                                                                    <embed
                                                                        src={`${item.fileURL}#toolbar=0&navpanes=0&scrollbar=0`}
                                                                        type="application/pdf"
                                                                        width="100%"
                                                                        height="450px"
                                                                        className="cursor-pointer"
                                                                        onClick={() => openPdfInBrowserOrLightbox(item?.fileURL)}
                                                                    />
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: 0,
                                                                            left: 0,
                                                                            width: "96%",
                                                                            height: "100%",
                                                                            backgroundColor: "transparent",
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            ) :
                                                                /* MSG/EML thumbnail logic */
                                                                isMsgOrEmlFile ? (
                                                                    <div
                                                                        onClick={() => openPdfInBrowserOrLightbox(item?.fileURL)}
                                                                        style={{ cursor: "pointer", position: "relative", width: "100%", height: "100%" }}
                                                                        data-tooltip-content='Click to view the receipt.' data-tooltip-id="clickReceipt"
                                                                    >
                                                                        <div
                                                                            className="d-flex flex-column align-items-center justify-content-center p-3"
                                                                            style={{
                                                                                height: "450px",
                                                                                backgroundColor: "#f8f9fa",
                                                                                border: "1px solid #e0e0e0",
                                                                                overflow: "hidden"
                                                                            }}
                                                                        >
                                                                            <FaEnvelope className="text-primary" style={{ fontSize: "64px", flexShrink: 0 }} />
                                                                            <h6
                                                                                className="mt-3 text-center px-2 w-100"
                                                                                style={{
                                                                                    wordWrap: "break-word",
                                                                                    overflowWrap: "break-word",
                                                                                    wordBreak: "break-word",
                                                                                    maxWidth: "100%",
                                                                                    display: "-webkit-box",
                                                                                    WebkitLineClamp: 3,
                                                                                    WebkitBoxOrient: "vertical",
                                                                                    overflow: "hidden",
                                                                                    textOverflow: "ellipsis",
                                                                                    lineHeight: "1.3"
                                                                                }}
                                                                            >
                                                                                {fileName}
                                                                            </h6>
                                                                            <span className="badge bg-primary mt-2" style={{ flexShrink: 0 }}>
                                                                                {getFileExtension(item.fileURL)}
                                                                            </span>
                                                                            <p className="text-muted mt-2 mb-0" style={{ flexShrink: 0 }}>
                                                                                <small>Click to view</small>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ) :
                                                                    /* HEIC logic */
                                                                    isHeicFileCheck ? (
                                                                        <div
                                                                            onClick={() => openPdfInBrowserOrLightbox(item?.fileURL)}
                                                                            style={{ cursor: "pointer", position: "relative", width: "100%", height: "100%" }}
                                                                            data-tooltip-content='Click to view the receipt.' data-tooltip-id="clickReceipt"
                                                                        >
                                                                            {heicPreviewUrl ? (
                                                                                <img
                                                                                    className="card-img-top w-100 cursor-pointer"
                                                                                    src={heicPreviewUrl}
                                                                                    height={450}
                                                                                    onClick={() => openPdfInBrowserOrLightbox(item?.fileURL)}
                                                                                    alt="HEIC preview"
                                                                                    style={{ objectFit: 'contain' }}
                                                                                />
                                                                            ) : (
                                                                                <div
                                                                                    className="d-flex flex-column align-items-center justify-content-center p-3"
                                                                                    style={{
                                                                                        height: "450px",
                                                                                        backgroundColor: "#f8f9fa",
                                                                                        border: "1px solid #e0e0e0",
                                                                                        overflow: "hidden"
                                                                                    }}
                                                                                >
                                                                                    <FaFileAlt className="text-warning" style={{ fontSize: "64px", flexShrink: 0 }} />
                                                                                    <h6
                                                                                        className="mt-3 text-center px-2 w-100"
                                                                                        style={{
                                                                                            wordWrap: "break-word",
                                                                                            overflowWrap: "break-word",
                                                                                            wordBreak: "break-word",
                                                                                            maxWidth: "100%",
                                                                                            display: "-webkit-box",
                                                                                            WebkitLineClamp: 3,
                                                                                            WebkitBoxOrient: "vertical",
                                                                                            overflow: "hidden",
                                                                                            textOverflow: "ellipsis",
                                                                                            lineHeight: "1.3"
                                                                                        }}
                                                                                    >
                                                                                        {fileName}
                                                                                    </h6>
                                                                                    <span className="badge bg-warning mt-2" style={{ flexShrink: 0 }}>
                                                                                        {getFileExtension(item.fileURL)}
                                                                                    </span>
                                                                                    <p className="text-muted mt-2 mb-0" style={{ flexShrink: 0 }}>
                                                                                        <small>Converting preview...</small>
                                                                                    </p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) :
                                                                        /* Regular image logic */
                                                                        (
                                                                            <img className="card-img-top w-100 cursor-pointer" src={item.fileURL}
                                                                                height={450}
                                                                                onClick={() => openPdfInBrowserOrLightbox(item?.fileURL)}
                                                                                onError={(e) => {
                                                                                    e.currentTarget.onerror = null;
                                                                                    e.currentTarget.src = 'media/svg/files/blank-image.svg';
                                                                                }}
                                                                                alt="Card image cap"
                                                                                data-tooltip-content='Click to view the receipt.' data-tooltip-id="clickReceipt" />
                                                                        )}
                                                        </div>
                                                        <div className="p-2">
                                                            {item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4 ? <>
                                                                <div className='row g-1 mb-1'>
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0 d-flex align-items-center">{labelKey.reportId}:
                                                                                {item.trackingId &&
                                                                                    <FaInfoCircle className='ms-1 text-gray-600 cursor-pointer'
                                                                                        data-tooltip-id="reportId" data-tooltip-content='Report ID is clickable'
                                                                                    />
                                                                                }
                                                                            </h5>
                                                                            <p className='mb-0 text-truncate min-w-0'> {item.trackingId ?
                                                                                <span className='text-primary cursor-pointer'
                                                                                    onClick={() => {
                                                                                        expenseDetailHandler();
                                                                                        triggerexpenseReportData({ expenseId: item?.expenseId })
                                                                                        triggerItemsById({ ExpenseId: item?.expenseId })
                                                                                    }}
                                                                                >{item.trackingId}</span>
                                                                                : "N/A"}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.reportStatus}:</h5>
                                                                            <p className='mb-0 text-truncate min-w-0'>
                                                                                {item?.expenseStatus?.id > 0 ?
                                                                                    <ExpenseStatusComp
                                                                                        expenseStatusId={item?.expenseStatus?.id}
                                                                                        expenseStatusTitle={item?.expenseStatus?.title}
                                                                                    />
                                                                                    : "N/A"}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.reportTitle}:</h5>
                                                                            <p className='mb-0 text-truncate min-w-0' title={item.reportTitle || ''}>
                                                                                {item.reportTitle || 'N/A'}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </> : null
                                                            }
                                                            {item.ocrReadStatus != 5 &&
                                                                <fieldset className="border border-gray-300 rounded-3 p-1 mb-1">
                                                                    <legend className="fs-7 fw-bold text-gray-800 w-auto px-2 mb-0 float-none">
                                                                        Item Details
                                                                    </legend>
                                                                    <div className='row g-1'>
                                                                        <div className='col-6'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.paymentMethod}:</h5>
                                                                                <p className='mb-0 text-truncate min-w-0'>
                                                                                    {item?.expensePayment?.id > 0 ? item.expensePayment?.title : "N/A"}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                                                                                            <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0 d-flex align-items-center">Category:
                                                                                {item.category &&
                                                                                    <FaInfoCircle className='ms-1 text-gray-600 cursor-pointer'
                                                                                        data-tooltip-id="lineItem" data-tooltip-content='Line Item Category is clickable'
                                                                                    />
                                                                                }
                                                                            </h5>
                                                                            <p className='mb-0 text-truncate min-w-0'> {item.category ?
                                                                                <span className='text-primary cursor-pointer'
                                                                                    onClick={() => handleViewLineItem(getEffectiveLineItemId(item))}
                                                                                >{item.category}</span> : "N/A"}</p>
                                                                        </div>
                                                                    </div>
                                                                        <div className='col-6'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.vendor}:</h5>
                                                                                <p className='mb-0 text-truncate min-w-0'> {item.itemVendorName || item.vendorName || 'N/A'}</p>
                                                                            </div>
                                                                        </div>
                                                                        <div className='col-6'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.invoiceNo}:</h5>
                                                                                <p className='mb-0 text-truncate min-w-0'> {item.invoiceNo ? item.invoiceNo : "N/A"}</p>
                                                                            </div>
                                                                        </div>

                                                                        <div className='col-6'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">Item Date:</h5>
                                                                                <p className='mb-0 text-truncate min-w-0'> {moment(item.expenseItemDate).format('MM-DD-YYYY')}</p>
                                                                            </div>
                                                                        </div>
                                                                        <div className='col-6'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.amount}:</h5>
                                                                                <p className='mb-0 text-truncate min-w-0'> <CurrencyComp amount={item.expenseItemAmount} /></p>
                                                                            </div>
                                                                        </div>
                                                                        <div className='col-6'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">Source:</h5>
                                                                                <span className='mb-0'>
                                                                                    {item.lineItemRecordSourceId === constraintConfig.recordSourceId.WebApp ?
                                                                                        <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1 ss-source-icon'
                                                                                            data-tooltip-id="web-source" data-tooltip-content='Website' />
                                                                                        : item.lineItemRecordSourceId === constraintConfig.recordSourceId.Mobile ?
                                                                                            <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1 ss-source-icon'
                                                                                                data-tooltip-id="mobile-source" data-tooltip-content='Mobile' /> :
                                                                                            item?.lineItemRecordSourceId === constraintConfig.recordSourceId.BankFeed ?
                                                                                                <KTSVG path="/media/icons/figmaIcons/Bank.svg" className='svg-icon-1 ss-source-icon'
                                                                                                    data-tooltip-id="bank-source" data-tooltip-content='Bank' />
                                                                                                : item?.lineItemRecordSourceId === constraintConfig.recordSourceId.GorillaExpense ?
                                                                                                    <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1 ms-4'
                                                                                                        data-tooltip-id="gorilla-source" data-tooltip-content='Gorilla Expense' /> : 'N/A'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </fieldset>
                                                            }
                                                            <fieldset className="border border-gray-300 rounded-3 p-1 mb-1">
                                                                <legend className="fs-7 fw-bold text-gray-800 w-auto px-2 mb-0 float-none">
                                                                    Receipt Details
                                                                </legend>
                                                                <div className='row g-1'>
                                                                    {item.ocrReadStatus === 5 && (
                                                                        <>
                                                                            <div className='col-6'>
                                                                                <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                    <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.invoiceNo}:</h5>
                                                                                    <p className='mb-0 text-truncate min-w-0'> {item.invoiceNo ? item.invoiceNo : "N/A"}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className='col-6'>
                                                                                <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                    <h5 className="card-title fs-7 m-0 flex-shrink-0">Source:</h5>
                                                                                    <span className='mb-0'>
                                                                                        {item.lineItemRecordSourceId === constraintConfig.recordSourceId.WebApp ?
                                                                                            <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1 ss-source-icon'
                                                                                                data-tooltip-id="web-source" data-tooltip-content='Website' />
                                                                                            : item.lineItemRecordSourceId === constraintConfig.recordSourceId.Mobile ?
                                                                                                <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1 ss-source-icon'
                                                                                                    data-tooltip-id="mobile-source" data-tooltip-content='Mobile' /> :
                                                                                                item?.lineItemRecordSourceId === constraintConfig.recordSourceId.BankFeed ?
                                                                                                    <KTSVG path="/media/icons/figmaIcons/Bank.svg" className='svg-icon-1 ss-source-icon'
                                                                                                        data-tooltip-id="bank-source" data-tooltip-content='Bank' />
                                                                                                    : item?.lineItemRecordSourceId === constraintConfig.recordSourceId.GorillaExpense ?
                                                                                                        <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1 ms-4'
                                                                                                            data-tooltip-id="gorilla-source" data-tooltip-content='Gorilla Expense' /> : 'N/A'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0">Receipt Date:</h5>
                                                                            {item.ocrReadStatus !== 0 &&
                                                                                <p className='mb-0 text-truncate min-w-0'> {moment(item.invoiceDate).format('MM-DD-YYYY hh:mm A')}</p>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0">{labelKey.amount}:</h5>
                                                                            <p className='mb-0 text-truncate min-w-0'> <CurrencyComp amount={item.amount} /></p>
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0">Uploaded From:</h5>
                                                                            <span className='mb-0'>
                                                                                {item.recordSourceID === constraintConfig.recordSourceId.WebApp ?
                                                                                    <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1 ss-source-icon'
                                                                                        data-tooltip-id="web-source" data-tooltip-content='Website' />
                                                                                    : item.recordSourceID === constraintConfig.recordSourceId.Mobile ?
                                                                                        <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1 ss-source-icon'
                                                                                            data-tooltip-id="mobile-source" data-tooltip-content='Mobile' /> :
                                                                                        item?.recordSourceId === constraintConfig.recordSourceId.GorillaExpense ?
                                                                                            <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1 ms-4'
                                                                                                data-tooltip-id="gorilla-source" data-tooltip-content='Gorilla Expense' /> : 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <div className='d-flex align-items-center gap-1 mt-1 min-w-0'>
                                                                            <h5 className="card-title fs-7 m-0 flex-shrink-0">Uploaded Date:</h5>
                                                                            <p className='mb-0 text-truncate min-w-0'><DateTimeComp formattedDate={item.createdDT} /> </p>
                                                                        </div>
                                                                    </div>
                                                                    {(isMsgOrEmlFile || isHeicFileCheck) ? (
                                                                        <>
                                                                            <div className='col-6'>
                                                                                <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                    <h5 className="card-title fs-7 m-0 flex-shrink-0">Description:</h5>
                                                                                    <p className='mb-0 text-truncate min-w-0' title={item.readDocShortDesc || ''}>{item.readDocShortDesc || 'N/A'}</p>
                                                                                </div>
                                                                            </div>
                                                                            <div className='col-6'>
                                                                                <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                    <h5 className="card-title fs-7 m-0 flex-shrink-0">File Type:</h5>
                                                                                    <span className={`badge ${isHeicFileCheck ? 'bg-warning' : 'bg-info'}`}>
                                                                                        {getFileExtension(item.fileURL)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <div className='col-12'>
                                                                            <div className='d-flex align-items-start gap-1 mt-1 min-w-0'>
                                                                                <h5 className="card-title fs-7 m-0 flex-shrink-0">Description:</h5>
                                                                                <p className='mb-0 text-truncate min-w-0 flex-grow-1' title={item.readDocShortDesc || ''}>{item.readDocShortDesc || 'N/A'}</p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </fieldset>

                                                            {item.remarks &&
                                                                <div className='gap-2 mt-1'>
                                                                    <div className='mb-2'>
                                                                        <h5 className="card-title fs-7 m-0 w-150px d-flex align-items-center">{labelKey.reason}:
                                                                            {item.ocrReadStatus === 5 &&
                                                                                <FaInfoCircle className='ms-2 text-gray-600 cursor-pointer'
                                                                                    data-tooltip-id="reason" data-tooltip-content='Reason is clickable'
                                                                                />
                                                                            }
                                                                        </h5>
                                                                    </div>
                                                                    {item.ocrReadStatus === 5 ?
                                                                        <p className='mb-0 text-primary cursor-pointer'
                                                                            onClick={() =>
                                                                                handleViewLineItem(
                                                                                    item?.usedExpenseItemId || getEffectiveLineItemId(item),
                                                                                    { includeDeleted: true }
                                                                                )
                                                                            }
                                                                        > {item.remarks ? item.remarks : "N/A"}</p>
                                                                        : <p className='mb-0' > {item.remarks ? item.remarks : "N/A"}</p>}
                                                                </div>
                                                            }
                                                        </div>
                                                        {item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4 || item?.ocrReadStatus === 5 || item?.ocrReadStatus === 2 ?
                                                            <div className="card-footer py-1">
                                                                <div className="row g-1">
                                                                    {item?.noOfRetry <= 2 && item?.ocrReadStatus === 2 && (
                                                                        <div className="col-lg-4 col-md-6 col-sm-12">
                                                                            <button
                                                                                className='btn btn-sm btn-warning w-100 fs-7 py-1'
                                                                                onClick={() => {
                                                                                    setAttachmentDeleteData(item?.invoiceDataDraftId);
                                                                                    retryInvoiceHandler();
                                                                                }}
                                                                            >
                                                                                Retry Invoice ({item?.noOfRetry})
                                                                            </button>
                                                                        </div>
                                                                    )}

                                                                    {(item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4) && item.trackingId && (
                                                                        <div className="col-lg-4 col-md-6 col-sm-12">
                                                                            <button
                                                                                className='btn btn-sm btn-primary w-100 fs-7 py-1'
                                                                                onClick={() => {
                                                                                    expenseDetailHandler();
                                                                                    triggerexpenseReportData({ expenseId: item?.expenseId });
                                                                                    triggerItemsById({ ExpenseId: item?.expenseId });
                                                                                }}
                                                                            >
                                                                                View Report
                                                                            </button>
                                                                        </div>
                                                                    )}

                                                                    {(item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4) && item.category && (
                                                                        <div className="col-lg-4 col-md-6 col-sm-12">
                                                                            <button
                                                                                className='btn btn-sm btn-primary w-100 fs-7 py-1'
                                                                                onClick={() => handleViewLineItem(item?.expenseItemId || getEffectiveLineItemId(item))}
                                                                            >
                                                                                View Line Item
                                                                            </button>
                                                                        </div>
                                                                    )}

                                                                    {item.ocrReadStatus === 5 && (
                                                                        <div className="col-lg-6 col-md-6 col-sm-12">
                                                                            <button
                                                                                className='btn btn-sm btn-primary w-100 fs-7 py-1'
                                                                                onClick={() =>
                                                                                    handleViewLineItem(
                                                                                        item?.usedExpenseItemId || getEffectiveLineItemId(item),
                                                                                        { includeDeleted: true }
                                                                                    )
                                                                                }
                                                                            >
                                                                                View Used Line Item
                                                                            </button>
                                                                        </div>
                                                                    )}

                                                                    {(!item?.usedExpenseItemId && (item.expenseId == null || item.ocrReadStatus == 3)) && (
                                                                        <div className="col-lg-4 col-md-6 col-sm-12">
                                                                            <button
                                                                                type="button"
                                                                                className='btn btn-sm btn-info w-100 fs-7 py-1'
                                                                                onClick={() => {
                                                                                    triggerItemById({ expenseItemId: item?.expenseItemId });
                                                                                    setViewOnlyData(false);
                                                                                    expenseItemModalHandler();
                                                                                }}
                                                                            >
                                                                                Edit Line Item
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            : null
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </>
                                ) : (
                                    <NoRecordFound />
                                )}
                            </>
                        )}
                        {isLoading && (
                            <LoadingComponent />
                        )}
                    </div>
                </div>
            </div >
        </>
    );
}

export default Invoices;